import { Form, Formik } from "formik";
import React, { useContext } from "react";
import { useMessageSource } from "react-message-source";
import ApplicationPageContent from "../../ui/layout/page/content/application";
import ContentFooter from "../../ui/layout/page/content/footer";
import NavigationBar from "../../ui/layout/page/content/footer/navigation-bar";
import FlowHeader from "../../ui/widgets/flow-header";
import ProductImage from "../shared/components/product-image";
import AdminProvider from "./adminContext";
import { RegulatoryInformationModel } from "./types";
import {
  useRegulatoryInformationSoleProprietorshipValidationSchema,
  useRegulatoryInformationValidationSchema,
} from "./validator";
import { LanguageContext } from "../../context/languageContext";
import InvalidFormError, {
  isSubmitErrorMessageShown,
} from "../../ui/layout/page/content/footer/form-error/invalid-form-error";
import { CancelApplicationContext } from "../../context/cancelApplicationContext";
import { ProductContext } from "../../context/product-context/productContext";
import { BeneficialOwner } from "./beneficial-owner";

import "./styles.scss";
import { CompanyClassificationComponent } from "./company-classification";
import {
  isCompanyClassificationBeneficialOwnerCase,
  isCompanyClassificationControllingOwnersCase,
  isNoMoreInfoNeededCaseTrue,
} from "./helpers";
import NoInformationNeeded from "./no-information-needed";
import SectionFullWidthContainer from "../../ui/layout/page/forms-container/section-full-width-container";
import { ControllerIdentificationComponent } from "./controller-identification";
import { ControllingOwners } from "./controlling-owners";
import { CompanyOwnershipQuestions } from "./company-ownership-questions";

interface RegulatoryInformationProps {
  initial: RegulatoryInformationModel;
  context: {
    isSoleProprietorship: boolean;
    companyName: string;
  };
  next: (model: RegulatoryInformationModel) => void;
  previous: () => void;
}

export default function RegulatoryInformation({
  initial,
  context,
  next,
  previous,
}: RegulatoryInformationProps): React.JSX.Element {
  const { getMessage } = useMessageSource();
  const { setCancelDialogActive } = useContext(CancelApplicationContext);
  const { language } = useContext(LanguageContext);
  const { selectedProduct } = useContext(ProductContext);

  const { isSoleProprietorship } = context;

  const validationSchemaSoleProprietorship =
    useRegulatoryInformationSoleProprietorshipValidationSchema();

  const validationSchemaNotSoleProprietorship =
    useRegulatoryInformationValidationSchema();

  return (
    <AdminProvider admin={initial.admin}>
      <FlowHeader
        title={getMessage("FlowHeader.basicAccountApplication")}
        image={<ProductImage product={selectedProduct?.productRef} />}
        subTitle={context.companyName}
      />
      <ApplicationPageContent>
        <Formik
          key={language}
          enableReinitialize
          initialValues={initial}
          validationSchema={
            isSoleProprietorship
              ? validationSchemaSoleProprietorship
              : validationSchemaNotSoleProprietorship
          }
          onSubmit={async (values, formikActions) => {
            await next(values);
            formikActions.setSubmitting(false);
          }}
        >
          {({ values, handleSubmit, isSubmitting, errors, submitCount }) => {
            return (
              <Form>
                {context.isSoleProprietorship ? (
                  <BeneficialOwner
                    isOwnerSameAsAdmin={values.isOwnerSameAsAdmin}
                    beneficialOwner={values.beneficialOwner}
                  />
                ) : (
                  <>
                    <CompanyOwnershipQuestions />
                    <CompanyClassificationComponent />
                    {isCompanyClassificationBeneficialOwnerCase(
                      values.companyClassification,
                      context.isSoleProprietorship,
                    ) && (
                      <SectionFullWidthContainer hasAdditionalTopMargin>
                        <BeneficialOwner
                          isOwnerSameAsAdmin={values.isOwnerSameAsAdmin}
                          beneficialOwner={values.beneficialOwner}
                        />
                      </SectionFullWidthContainer>
                    )}
                    {isCompanyClassificationControllingOwnersCase(
                      values.companyClassification,
                    ) && (
                      <SectionFullWidthContainer hasAdditionalTopMargin>
                        <ControllerIdentificationComponent />
                        <ControllingOwners
                          isOwnerSameAsAdmin={values.isOwnerSameAsAdmin}
                          controllingOwners={values.controllingOwners}
                          maxOwnersAllowed={4}
                        />
                      </SectionFullWidthContainer>
                    )}

                    {isNoMoreInfoNeededCaseTrue(
                      values.companyClassification,
                    ) && <NoInformationNeeded />}
                  </>
                )}

                <ContentFooter>
                  <InvalidFormError
                    isDisplayed={isSubmitErrorMessageShown(submitCount, errors)}
                  />
                  <NavigationBar
                    onCancel={() => setCancelDialogActive(true)}
                    cancelButtonText={getMessage("Onboarding.cancel")}
                    isSubmitting={isSubmitting}
                    onBack={previous}
                    onNext={handleSubmit}
                    backButtonText={getMessage("Onboarding.back")}
                    nextButtonText={getMessage("Onboarding.continue")}
                  />
                </ContentFooter>
              </Form>
            );
          }}
        </Formik>
      </ApplicationPageContent>
    </AdminProvider>
  );
}
